import { ignoreVisits as updateIgnoreVists } from '@va/dashboard/actions/app';
import { useIgnoredIPs } from '@va/dashboard/feature/general-settings';
import { getIgnoreVisits } from '@va/dashboard/selectors/app';
import { getWebsite } from '@va/dashboard/selectors/core';
import { getSupportCenterDomain } from '@va/dashboard/selectors/customization';
import { SettingsHeading } from '@va/dashboard/ui/pages';
import { PauseIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { contributorRole } from '@va/standalone/shared/selectors';
import {
  Button,
  CopyableSection,
  InputWithSubsection,
  Paragraph,
  ToggleActionCard,
  paragraphSizes,
} from '@va/ui/design-system';
import Config from 'Config';
import { useDispatch, useSelector } from 'react-redux';
import IgnoreVisitsIFrame from './IgnoreVisitsIFrame';

type IgnoreVisitsDataType = {
  ignore?: boolean;
  inProgress: boolean;
  isAllIgnored?: boolean;
  isLoaded: boolean;
  isIgnored: boolean;
  link: string;
};

export const IgnoreOwnVisitsCardWix = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const supportCenterDomain = useSelector(getSupportCenterDomain);
  const isContributor = useSelector(contributorRole);
  const website = useSelector(getWebsite) as any;
  const ignoreVisitsKey = website.ignoreVisitsKey;
  const ignoreVisits: IgnoreVisitsDataType = useSelector(getIgnoreVisits);
  const baseUrl = Config && Config.wixBaseUrl.replace(/\/+$/, '');
  const ignoreLink = `${baseUrl}/visit-settings?md5=${ignoreVisitsKey}&url=${website.url}`;

  const onToggle = () => {
    dispatch(updateIgnoreVists(!ignoreVisits.ignore));
  };

  const {
    onSubmit,
    onRemove,
    onChange,
    submitSuccess,
    isLoadingCurrentIgnoredIPs: isLoading,
    inputValue,
    ignoredIPs,
    hasMadeChanges,
    error,
    onSave,
    isLoadingUpdateCall,
  } = useIgnoredIPs();

  return (
    <div className='flex flex-col gap-4 items-start'>
      <SettingsHeading
        title={translate('card.ignoreSelfVisits.title')}
        tooltip={translate('card.ignoreSelfVisits.tooltip', { supportCenterDomain })}
      />
      <div className='flex flex-col w-full gap-4'>
        <ToggleActionCard
          disabled={isContributor}
          infoTooltip={isContributor ? translate('tooltips.contributorRole') : undefined}
          title={translate('card.ignoreSelfVisits.ignoreText')}
          name='browserTracking'
          subtitle={translate('card.ignoreSelfVisits.info')}
          value={!!ignoreVisits?.ignore}
          handleChange={onToggle}
          icon={() => (
            <div className='flex items-center justify-center w-12 h-12 rounded-full bg-white'>
              <PauseIcon color='#3C3C3C' className='w-6 h-6' />
            </div>
          )}
        />
        <CopyableSection
          value={ignoreLink}
          info={translate('card.ignoreSelfVisits.ignoreLinkText')}
          label={translate('card.ignoreSelfVisits.ignoreTrackingLink')}
          copyBtnOptions={{ hidden: true }}
        />
        <InputWithSubsection
          disabled={isContributor}
          infoTooltip={isContributor ? translate('tooltips.contributorRole') : undefined}
          isLoading={isLoading}
          onSubmit={onSubmit}
          onRemove={onRemove}
          listItems={ignoredIPs ?? []}
          itemsLabel={translate('card.ignoreSelfVisits.items.label')}
          successSubmit={submitSuccess}
          inputProps={{
            value: inputValue,
            onChange: onChange,
            placeholder: translate('card.ignoreSelfVisits.ignoreIpAddressOrRangeInput.placeholder'),
            name: 'ignoredIp',
            label: translate('card.ignoreSelfVisits.ignoreIpAddressOrRangeInput.label'),
          }}
          error={error}
        />
        {hasMadeChanges && (
          <Button disabled={isLoadingUpdateCall} onClick={onSave} text={translate('button.saveChanges')} />
        )}
        <IgnoreVisitsIFrame ignore={!!ignoreVisits.ignore} hash={ignoreVisitsKey} />
      </div>
      <Paragraph size={paragraphSizes.tiny} className='text-gray-devil -mt-1 ml-3'>
        {translate('card.ignoreSelfVisists.additionalInfo', { supportCenterDomain })}
      </Paragraph>
    </div>
  );
};
